import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound } from "lucide-react";
import { formatPostDateShort } from "@/lib/date";

const AdminUsers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault();
    const u = username.trim().toLowerCase();
    if (!u || !password) return toast.error("Username e password richiesti");
    setBusy(true);
    try {
      await api.post("/admin/users", { username: u, password });
      toast.success(`Lettore "${u}" creato`);
      setUsername("");
      setPassword("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (id, uname) => {
    const newPw = window.prompt(`Nuova password per "${uname}" (min 6 caratteri):`);
    if (!newPw) return;
    try {
      await api.put(`/admin/users/${id}`, { password: newPw });
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const remove = async (id, uname) => {
    if (!window.confirm(`Eliminare il lettore "${uname}"?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Lettore eliminato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <AdminShell title="Lettori">
      <div className="max-w-3xl">
        <p className="font-serif italic text-base sm:text-lg text-[#4a4843] mb-8 leading-relaxed">
          I lettori autenticati possono leggere i post marcati come <em>riservati</em>. Crea qui i loro accessi: gli utenti potranno entrare da <span className="font-sans not-italic text-xs tracking-[0.2em] uppercase">/accedi</span> con username e password.
        </p>

        <form
          onSubmit={create}
          data-testid="users-create-form"
          className="border border-[#e5e1d8] p-5 sm:p-6 mb-10 bg-[#f9f8f6]"
        >
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-4">Nuovo lettore</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mb-1">Username</label>
              <input
                data-testid="users-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                placeholder="es. mario"
                className="w-full bg-transparent border-0 border-b border-[#1a1918] py-2 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
              />
            </div>
            <div>
              <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mb-1">Password</label>
              <input
                data-testid="users-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min 6 caratteri"
                className="w-full bg-transparent border-0 border-b border-[#1a1918] py-2 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="users-create-submit"
            disabled={busy}
            className="flex items-center gap-2 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#4a4843] transition-colors px-5 py-2.5 font-sans text-xs tracking-[0.3em] uppercase disabled:opacity-50"
          >
            <UserPlus size={14} /> {busy ? "creazione…" : "crea lettore"}
          </button>
        </form>

        <div>
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-4">
            Lettori esistenti{!loading ? ` (${items.length})` : ""}
          </p>
          {loading ? (
            <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
          ) : items.length === 0 ? (
            <p data-testid="users-empty" className="font-serif italic text-[#4a4843] py-10 text-center border border-dashed border-[#e5e1d8]">
              Nessun lettore creato. Aggiungi il primo qui sopra.
            </p>
          ) : (
            <ul className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]">
              {items.map((u) => (
                <li
                  key={u.id}
                  data-testid={`user-row-${u.username}`}
                  className="flex flex-wrap items-center justify-between gap-3 py-4"
                >
                  <div className="min-w-0">
                    <p className="font-serif text-lg text-[#1a1918]">{u.username}</p>
                    <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-0.5">
                      creato il {formatPostDateShort(u.created_at)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`user-reset-${u.username}`}
                      onClick={() => resetPassword(u.id, u.username)}
                      className="flex items-center gap-2 border border-[#1a1918] hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors px-3 py-2 font-sans text-[10px] tracking-[0.25em] uppercase"
                    >
                      <KeyRound size={12} /> nuova password
                    </button>
                    <button
                      data-testid={`user-delete-${u.username}`}
                      onClick={() => remove(u.id, u.username)}
                      className="flex items-center gap-2 border border-[#9e4747] text-[#9e4747] hover:bg-[#9e4747] hover:text-[#f9f8f6] transition-colors px-3 py-2 font-sans text-[10px] tracking-[0.25em] uppercase"
                    >
                      <Trash2 size={12} /> elimina
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminUsers;
